import React, {useCallback, useEffect, useState} from "react";
import CustomerProfileInformationSection from "./profile-info";
import CustomerMembersSection from "./members";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import Api from "../../../../../../core/services/api/api";
import {CustomerMemberShipNames} from "../../../../../../core/constants/enums";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import useCachedPriceLevelsDictionaryController from "../../../../../hooks/use-price-level-dictionary";
import useAccessLevels from "../../../../../hooks/use-access-levels";
import {useHistory, useParams} from "react-router-dom";
import {Box, Button, CircularProgress, Skeleton, Typography} from "@mui/material";
import Grid2 from "@mui/material/Unstable_Grid2";
import CustomerSettingsSection from "./settings";
import CustomerAccountSummary from "./account-summary";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";
import {DeleteRounded} from "@mui/icons-material";
import {useDispatch} from "react-redux";
import {setReduxDialogState} from "../../../../../redux/slices/dialogs/actions";
import ReduxStoreRoutes from "../../../../../../core/models/static/routes/redux-store-routes";
import {routes} from "../../../../../../core/models/static/routes/app-routes";
import CustomerStatisticsSection from "./statistics";


const CustomerProfileView = () => {
    const params = useParams();
    const history = useHistory();
    const operatorAccessLevels = useAccessLevels();
    const [loading, setLoading] = useState(true);
    const [customer, setCustomer] = useState({});
    const [bypassInfo, setBypassInfo] = useState();
    const [loadingFormData, setLoadingFormData] = useState(true);
    const [accountSummary, setAccountSummary] = useState({});
    const [loadingAccountSummary, setLoadingAccountSummary] = useState(true);
    const [deleting, setDeleting] = useState(false);
    const [formData, setFormData] = useState({
        countries: [],
        provinces: [],
        statuses: [],
        paymentTerms: [],
        priceLevels: [],
        searchLimitTypes: [],
    });
    const [territory, setTerritory] = useState(null);
    const [accountManager, setAccountManager] = useState(null);
    const isMounted = useIsMounted();
    const dispatch = useDispatch();
    const {configureNotebookTags} = useConfigureNotebook('customer-profile-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();
    const {getPriceLevelDictionary} = useCachedPriceLevelsDictionaryController();

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        if (!customer?.code)
            return;
        configureNotebookTags((noteTags) => noteTags.customerProfile(customer.code));
        configureBehavioralTool((tags) => tags.customerProfile(customer.code));
    }, [configureBehavioralTool, configureNotebookTags, customer.code]);

    /**
     * Gets all the form data necessary and used in the process of editing the profile information from the server.
     */
    const getProfileFormData = useCallback(async () => {
        setLoadingFormData(true);
        const [getUserProfileFormResponse, priceLevelDict] = await Promise.all([
            Api.getUserProfileForm(),
            getPriceLevelDictionary(),
        ])
        if (!isMounted())
            return;
        setLoadingFormData(false);
        if (getUserProfileFormResponse?.resultFlag) {
            setFormData({
                countries: getUserProfileFormResponse?.data?.countries ?? [],
                provinces: getUserProfileFormResponse?.data?.provinces ?? [],
                statuses: getUserProfileFormResponse?.data?.membershipTypes?.filter(e => e.name !== CustomerMemberShipNames.onHoldCompanyLost && e.name !== CustomerMemberShipNames.onHold) ?? [],
                priceLevels: getUserProfileFormResponse?.data?.priceLevels?.map(p => ({
                    ...p,
                    title: priceLevelDict?.find(d => d.priceLevelId === p.id)?.title ?? p.title,
                })) ?? [],
                paymentTerms: getUserProfileFormResponse?.data?.paymentTerms,
                searchLimitTypes: getUserProfileFormResponse?.data?.searchLimitsPeriods?.filter(e => e.minutesCount !== null),
            })
        }
    }, [getPriceLevelDictionary, isMounted])

    /**
     * As soon as the component is mounted, fetches form data.
     */
    useEffect(() => {
        void getProfileFormData();
    }, [getProfileFormData])


    /**
     * Fetches the quote bypass information.
     * @type {(function(): Promise<void>)}
     */
    const getQuoteBypassInfo = useCallback(async () => {
        const bypassResponse = await Api.getQuoteBypassRuleInfoForCustomer(params.customerId);
        if (!isMounted())
            return;
        if (bypassResponse?.resultFlag) {
            setBypassInfo(bypassResponse.data);
        }
    }, [isMounted, params.customerId]);

    /**
     * Fetches the customer information.
     * @type {(function(): Promise<void>)}
     */
    const getCustomerInformation = useCallback(async () => {
        setLoading(true);
        const response = await Api.getCustomerFullInformation(params.customerId);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setCustomer(response.data ?? {});
            setTerritory(response.data?.territoryCode ? {code: response.data?.territoryCode, name: ""} : null);
            setAccountManager(response.data?.accountManagerCode ? {
                code: response.data?.accountManagerCode,
                name: response.data?.accountManagerName,
            } : null);
            if (operatorAccessLevels.packages.base.manageCustomerLimitations && operatorAccessLevels.packages.sales.quoteBypass) {
                await getQuoteBypassInfo();
            }
        } else {
            history.goBack();
        }
        if (!isMounted())
            return;
        setLoading(false);
    }, [params.customerId, isMounted, operatorAccessLevels.packages.base.manageCustomerLimitations, operatorAccessLevels.packages.sales.quoteBypass, getQuoteBypassInfo, history]);

    /**
     * As soon as the component is mounted, fetches the customer information.
     * @type {(function(): Promise<void>)}
     */
    useEffect(() => {
        void getCustomerInformation();
    }, [getCustomerInformation]);


    /**
     * Fetches the account summary information.
     * @type {(function(): Promise<void>)}
     */
    const fetchAccountSummary = useCallback(async () => {
        setLoadingAccountSummary(true);
        const response = await Api.getCustomerAccountInformation(params.customerId);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setAccountSummary(response.data);
        }
        setLoadingAccountSummary(false);
    }, [params.customerId, isMounted]);

    /**
     * Fetches the account summary information as soon as the component is mounted.
     */
    useEffect(() => {
        void fetchAccountSummary();
    }, [fetchAccountSummary]);

    /**
     * Displays a confirmation dialog before removing a customer.
     * @type {(function(): void)}
     */
    const getRemoveConfirmation = useCallback(() => {
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.removeConfirmation, {
            open: true,
            data: null,
            removedEntryTitle: `${customer?.name ?? "Customer"}'s Profile? This action cannot be undone and will remove all associated data with this customer. Shall we proceed`,
            onClose: async (confirmed) => {
                if (!confirmed)
                    return;
                setDeleting(true);
                const response = await Api.removeCustomer(customer?.id);
                if (!isMounted())
                    return;
                setDeleting(false);
                if (response?.resultFlag) {
                    history.replace(routes.customers.list);
                }
                return response?.resultFlag ?? false
            }
        }));
    }, [customer?.name, customer?.id, dispatch, history, isMounted])

    return (
        <>
            <Box sx={{
                display: 'flex',
                marginBlockEnd: 4,
                gap: 2,
                alignItems: 'center',
                justifyContent: 'space-between'
            }}>
                <Typography variant={'h6'} fontWeight={600}>
                    {customer?.name ?? "Customer"}'s Profile Information
                </Typography>
                <Box sx={{display: 'flex', gap: 0.5, alignItems: 'center', justifyContent: 'flex-end'}}>
                    {
                        operatorAccessLevels.packages.base.removeCustomer &&
                        <Button
                            variant={'outlined'}
                            color={'error'}
                            onClick={getRemoveConfirmation}
                            disabled={loading || loadingFormData || deleting}
                            startIcon={deleting ? <CircularProgress size={20}/> : <DeleteRounded/>}
                        >
                            {deleting ? 'Deleting...' : 'Delete Customer'}
                        </Button>
                    }
                </Box>
            </Box>
            <Grid2 container spacing={2} sx={{width: '100%'}}>
                <Grid2 xs={12} md={6}>
                    {
                        (loading || loadingFormData) &&
                        <Skeleton sx={{transform: 'none'}} height={'50vh'} width={'100%'}/>
                    }
                    {
                        !(loading || loadingFormData) &&
                        <CustomerProfileInformationSection
                            customer={customer}
                            setCustomer={setCustomer}
                            formData={formData}
                            deleting={deleting}
                        />
                    }

                </Grid2>
                <Grid2 xs={12} md={6}>
                    {
                        (loading || loadingFormData) &&
                        <Skeleton sx={{transform: 'none'}} height={'50vh'} width={'100%'}/>
                    }
                    {
                        !(loading || loadingFormData) &&
                        <CustomerAccountSummary
                            accountSummary={accountSummary}
                            loadingAccountSummary={loadingAccountSummary}
                            formData={formData}
                        />
                    }
                </Grid2>
                <Grid2 xs={12} {...(operatorAccessLevels.packages.dashboard.customerInformationStats && {
                    md: 5,
                    lg: 4
                })}>
                    <Grid2 container spacing={2} columns={12}>
                        <Grid2 xs={12}>
                            {
                                (loading || loadingFormData) &&
                                <Skeleton sx={{transform: 'none'}} height={'50vh'} width={'100%'}/>
                            }
                            {
                                !(loading || loadingFormData) &&
                                <CustomerMembersSection
                                    customer={customer}
                                    formData={formData}
                                    deleting={deleting}
                                />
                            }
                        </Grid2>
                        {
                            operatorAccessLevels.packages.base.manageCustomerLimitations &&
                            <Grid2 xs={12}>
                                {
                                    (loading || loadingFormData) &&
                                    <Skeleton sx={{transform: 'none'}} height={'50vh'} width={'100%'}/>
                                }
                                {
                                    !(loading || loadingFormData) &&
                                    <CustomerSettingsSection
                                        customer={customer}
                                        setCustomer={setCustomer}
                                        bypass={bypassInfo}
                                        setBypass={setBypassInfo}
                                        formData={formData}
                                        refetchAccounting={fetchAccountSummary}
                                        deleting={deleting}
                                        territory={territory}
                                        setTerritory={setTerritory}
                                        accountManager={accountManager}
                                        setAccountManager={setAccountManager}
                                    />
                                }
                            </Grid2>
                        }
                    </Grid2>
                </Grid2>
                {
                    operatorAccessLevels.packages.dashboard.customerInformationStats &&
                    <Grid2 xs={12} md={7} lg={8}>
                        <CustomerStatisticsSection
                            customerCode={customer?.code}
                            customerName={customer?.name}
                            loading={loading}
                        />
                    </Grid2>
                }

            </Grid2>
        </>
    );
}


export default CustomerProfileView;
